import {getServicePath} from '@dazn/lp-web/src/utils/dazn/Service';
import {railTypeConfig} from './railConfig';
import {tileTypes} from '../constants/tile.constants';

const tileContentType = {
    DAZN: 'dazn',
    NFL: 'nfl'
};

const competitionIds = {
    NFL: 'wy3kluvb4efae1of0d8146c1'
};

export const tileTypeConfig = {
    SKELETON: {
        type: 'SKELETON',
        aspectRatio: {
            width: 16,
            height: 9
        }
    },
    STANDARD: {
        type: 'STANDARD',
        aspectRatio: {
            width: 16,
            height: 9
        }
    },
    PORTRAIT: {
        type: 'PORTRAIT',
        aspectRatio: {
            width: 2,
            height: 3
        }
    }
};

function getContentType(tile: any) {
    if (competitionIds.NFL === tile?.Competition?.Id) {
        return tileContentType.NFL;
    }
    return tileContentType.DAZN;
}

const getImageHeightBasedOnAspectRatio = (width: any, aspectRatio: number) => {
    return Math.floor(width / aspectRatio);
};

const getTileWidth = (railTypeInfo: any) => {
    return railTypeInfo?.width || railTypeConfig.Standard.width;
};

let imageQualityPercentage: number;
const getImageLoadQuality = () => {
    if (imageQualityPercentage !== undefined) {
        return imageQualityPercentage;
    }
    try {
        imageQualityPercentage =
            window.dazn.features.getVariableIntegerAnonymous(
                'f_ar_catalog_image_quality',
                'imageQuality'
            );
        return imageQualityPercentage;
    } catch (e) {
        return 85;
    }
};

export const getTileImage = ({
    imageId,
    componentType,
    isLogo
}: {
    imageId: string | undefined;
    componentType: string;
    isLogo?: boolean;
}): string | null => {
    if (!imageId) return null;
    const imgBasePath = getServicePath('img', 2);
    const railTypeInfo = railTypeConfig[componentType];
    const aspectRatio =
        railTypeInfo?.aspectRatio || railTypeConfig.Standard.aspectRatio;
    const width = getTileWidth(railTypeInfo);
    const height = getImageHeightBasedOnAspectRatio(width, aspectRatio);
    const quality = getImageLoadQuality();

    if (isLogo) {
        return `${imgBasePath}/?id=${imageId}&quality=${quality}&width=448&height=258&resizeAction=contain&verticalAlignment=bottom&format=webp`;
    }
    return `${imgBasePath}/?id=${imageId}&quality=${quality}&width=${width}&height=${height}&resizeAction=fill&verticalAlignment=top&format=webp`;
};

export function railProxy(rail: any, railType: any) {
    return {
        id: rail.Id,
        title: rail.Title,
        description: rail.Description,
        tiles: rail?.Tiles?.map((tile: any) =>
            tileProxy(tile, rail.Id, railType)
        )
    };
}

export function tileProxy(tile: any, railId: string, componentType: string) {
    return {
        railId: railId || null,
        railType: componentType || null,
        id: tile.Id,
        assetId: tile.Videos.length > 0 ? tile.AssetId : null,
        eventId: tile.EventId,
        navParams: tile?.NavParams || tile?.ArticleNavParams,
        navigateTo:
            (tile?.NavigateTo && tile?.NavigateTo.toLowerCase()) ||
            (tile?.ArticleNavigateTo && tile?.ArticleNavigateTo.toLowerCase()),
        type: tile.Type,
        isVod: [
            tileTypes.CATCH_UP,
            tileTypes.COACHES,
            tileTypes.CONDENSED,
            tileTypes.AMERICAN_FOOTBALL_CONDENSED,
            tileTypes.AMERICAN_FOOTBALL_GAME_IN_40,
            tileTypes.HIGHLIGHTS,
            tileTypes.ON_DEMAND,
            tileTypes.FIBA_THREE_MINUTES_HIGHLIGHTS,
            tileTypes.FIBA_TEN_MINUTES_HIGHLIGHTS
        ].includes(tile.Type),
        title: tile.Title,
        label: tile.Label,
        description: tile.Description,
        start: tile.Start,
        end: tile.End,
        displayDate: tile.DisplayDate,
        imageUrl: getTileImage({
            imageId: tile.Image?.Id,
            componentType
        }),
        portraitImageUrl: getTileImage({
            imageId: tile?.PortraitImage?.Id,
            componentType
        }),
        logoUrl: getTileImage({
            imageId: tile?.LogoImage?.Id,
            componentType,
            isLogo: true
        }),
        competitionId: tile.Competition && tile.Competition.Id,
        competitionName: tile.Competition && tile.Competition.Title,
        related: tile.Related?.map((relatedTile: any) => {
            const relatedTileWithRailId = {
                ...relatedTile,
                railId
            };
            return tileProxy(relatedTileWithRailId, railId, componentType);
        }),
        entitlementIds: tile.EntitlementIds || [],
        contentType: getContentType(tile)
    };
}
